import { useState, useCallback, useRef } from "react";
import DvsEvaluationService, {
  AnnotationPoint,
} from "../../../_services/DvsEvaluationService";

export function useAnnotation() {
  // Use REFS for drawing data — imperative access, no stale closures.
  // This is the key difference from the old approach: React state updates
  // are async/batched, causing stale data in effects. Refs are synchronous.
  const loadedPointsRef = useRef<AnnotationPoint[]>([]);
  const strokeStackRef = useRef<AnnotationPoint[][]>([]);
  const redoStackRef = useRef<AnnotationPoint[][]>([]);
  const mappingIdRef = useRef<number>(0);
  const pageRef = useRef<number>(1);
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  // State ONLY for UI reactivity (toolbar button enabled/disabled)
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateUndoRedoState = useCallback(() => {
    setCanUndo(
      strokeStackRef.current.length > 0 ||
        loadedPointsRef.current.length > 0,
    );
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const getAllPoints = useCallback((): AnnotationPoint[] => {
    return [...loadedPointsRef.current, ...strokeStackRef.current.flat()];
  }, []);

  const loadPage = useCallback(
    async (mappingId: number, page: number): Promise<AnnotationPoint[]> => {
      // Wait for any pending stroke save before loading
      if (pendingSaveRef.current) {
        await pendingSaveRef.current;
        pendingSaveRef.current = null;
      }
      mappingIdRef.current = mappingId;
      pageRef.current = page;
      strokeStackRef.current = [];
      redoStackRef.current = [];
      try {
        const points = await DvsEvaluationService.getAnnotation(
          mappingId,
          page,
        );
        loadedPointsRef.current = points;
        updateUndoRedoState();
        return points;
      } catch (err) {
        console.error("Failed to load annotations:", err);
        loadedPointsRef.current = [];
        updateUndoRedoState();
        return [];
      }
    },
    [updateUndoRedoState],
  );

  const addStroke = useCallback(
    (points: AnnotationPoint[]) => {
      if (points.length === 0) return;
      strokeStackRef.current = [...strokeStackRef.current, points];
      redoStackRef.current = [];
      updateUndoRedoState();
      const savePromise = DvsEvaluationService.saveAnnotation(
        mappingIdRef.current,
        pageRef.current,
        points,
      ).catch((err) => {
        console.error("Failed to save annotation:", err);
      });
      pendingSaveRef.current = savePromise;
    },
    [updateUndoRedoState],
  );

  /** Undo last stroke. Returns all remaining points to draw. */
  const undo = useCallback(async (): Promise<AnnotationPoint[]> => {
    if (
      strokeStackRef.current.length === 0 &&
      loadedPointsRef.current.length === 0
    ) {
      return [];
    }
    // Optimistically move last stroke to redo stack
    const last =
      strokeStackRef.current[strokeStackRef.current.length - 1];
    if (last) {
      redoStackRef.current = [...redoStackRef.current, last];
      strokeStackRef.current = strokeStackRef.current.slice(0, -1);
    }
    try {
      const remaining = await DvsEvaluationService.undoAnnotation(
        mappingIdRef.current,
        pageRef.current,
      );
      loadedPointsRef.current = remaining;
      strokeStackRef.current = [];
      updateUndoRedoState();
      return remaining;
    } catch (err) {
      console.error("Failed to undo:", err);
      updateUndoRedoState();
      return getAllPoints();
    }
  }, [updateUndoRedoState, getAllPoints]);

  /** Redo last undone stroke. Returns all points to draw. */
  const redo = useCallback(async (): Promise<AnnotationPoint[]> => {
    if (redoStackRef.current.length === 0) return getAllPoints();
    const pointsToRedo =
      redoStackRef.current[redoStackRef.current.length - 1];
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    strokeStackRef.current = [...strokeStackRef.current, pointsToRedo];
    updateUndoRedoState();
    try {
      await DvsEvaluationService.saveAnnotation(
        mappingIdRef.current,
        pageRef.current,
        pointsToRedo,
      );
    } catch (err) {
      console.error("Failed to redo:", err);
    }
    return getAllPoints();
  }, [updateUndoRedoState, getAllPoints]);

  /** Clear all annotations on current page. Returns empty array. */
  const clearPage = useCallback(async (): Promise<AnnotationPoint[]> => {
    loadedPointsRef.current = [];
    strokeStackRef.current = [];
    redoStackRef.current = [];
    updateUndoRedoState();
    try {
      await DvsEvaluationService.clearAnnotation(
        mappingIdRef.current,
        pageRef.current,
      );
    } catch (err) {
      console.error("Failed to clear:", err);
    }
    return [];
  }, [updateUndoRedoState]);

  return {
    canUndo,
    canRedo,
    loadPage,
    addStroke,
    undo,
    redo,
    clearPage,
    getAllPoints,
  };
}
